import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface SequencerStats {
  totalCreators: number;
  totalSequences: number;
  totalDownloads: number;
  featuredCreators: number;
  avgSequencesPerCreator: number;
}

interface UseSequencerStatsResult {
  stats: SequencerStats;
  isLoading: boolean;
  error: string | null;
}

/**
 * Custom hook to fetch aggregate statistics about sequencers (creators) for hero section display.
 *
 * Calculates:
 * - Total number of creators (users with at least 1 sequence)
 * - Total number of sequences
 * - Total download count across all sequences
 * - Number of featured creators
 * - Average sequences per creator
 *
 * @returns SequencerStats object with loading and error states
 */
export function useSequencerStats(): UseSequencerStatsResult {
  const [stats, setStats] = useState<SequencerStats>({
    totalCreators: 0,
    totalSequences: 0,
    totalDownloads: 0,
    featuredCreators: 0,
    avgSequencesPerCreator: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadStats = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Fetch all sequences to calculate stats
        const { data: sequences, error: sequencesError } = await supabase
          .from('sequences')
          .select('user_id, download_count')
          .not('user_id', 'is', null);

        if (sequencesError) {
          console.error('Error fetching sequences:', sequencesError);
          throw new Error(sequencesError.message);
        }

        if (!sequences || sequences.length === 0) {
          setStats({
            totalCreators: 0,
            totalSequences: 0,
            totalDownloads: 0,
            featuredCreators: 0,
            avgSequencesPerCreator: 0,
          });
          setIsLoading(false);
          return;
        }

        // Calculate unique creators
        const uniqueCreators = new Set(sequences.map((s) => s.user_id));
        const totalCreators = uniqueCreators.size;

        // Calculate total downloads (sum of all download_count fields)
        const totalDownloads = sequences.reduce((sum, seq) => {
          return sum + (seq.download_count || 0);
        }, 0);

        // Get total sequences
        const totalSequences = sequences.length;

        // Fetch featured creators count
        const { count: featuredCount, error: featuredError } = await supabase
          .from('user_profiles')
          .select('*', { count: 'exact', head: true })
          .eq('featured', true);

        if (featuredError) {
          console.error('Error fetching featured creators:', featuredError);
          // Don't throw - just set featured count to 0
        }

        const featuredCreators = featuredCount || 0;

        // Calculate average sequences per creator
        const avgSequencesPerCreator =
          totalCreators > 0 ? Math.round(totalSequences / totalCreators) : 0;

        setStats({
          totalCreators,
          totalSequences,
          totalDownloads,
          featuredCreators,
          avgSequencesPerCreator,
        });

        console.log('Loaded sequencer stats:', {
          totalCreators,
          totalSequences,
          totalDownloads,
          featuredCreators,
          avgSequencesPerCreator,
        });
      } catch (err) {
        console.error('Error loading sequencer stats:', err);
        setError(err instanceof Error ? err.message : 'Failed to load sequencer stats');
        setStats({
          totalCreators: 0,
          totalSequences: 0,
          totalDownloads: 0,
          featuredCreators: 0,
          avgSequencesPerCreator: 0,
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadStats();
  }, []);

  return {
    stats,
    isLoading,
    error,
  };
}
